import type { Aspect, CreateProjectInput, Style } from "@dramapie/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError, createProject } from "../lib/api";

const ASPECT_OPTIONS: { value: Aspect; label: string }[] = [
  { value: "9:16", label: "9:16 竖屏" },
  { value: "16:9", label: "16:9 横屏" },
  { value: "1:1", label: "1:1 方形" },
];

const STYLE_OPTIONS: { value: Style; label: string }[] = [
  { value: "live_action", label: "真人实拍" },
  { value: "anime", label: "动画" },
];

export function NewProjectDialog({ onClose }: { onClose: () => void }) {
  const [title, setTitle] = useState("");
  const [aspect, setAspect] = useState<Aspect>("9:16");
  const [style, setStyle] = useState<Style>("live_action");
  const [titleError, setTitleError] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: (input: CreateProjectInput) => createProject(input),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      onClose();
      // 创建响应不带第一集 id，先让工作台用后端默认集（不带 ?ep=）。
      navigate(`/p/${created.id}`);
    },
  });

  useEffect(() => {
    titleInputRef.current?.focus();
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const mutationError =
    mutation.error instanceof ApiError ? mutation.error.message : mutation.error ? "创建失败，请重试" : null;

  return (
    <div
      className="dp-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-project-title"
        className="dp-dialog"
      >
        <div className="mb-5">
          <h2 id="new-project-title" className="font-display text-2xl font-medium text-fg">
            新建项目
          </h2>
          <p className="mt-1 text-sm text-muted">先定标题、画幅和风格，之后可以在项目里改。</p>
        </div>

        <form
          className="space-y-5"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = title.trim();
            if (!trimmed) {
              setTitleError("请填写项目标题");
              titleInputRef.current?.focus();
              return;
            }
            setTitleError(null);
            mutation.mutate({ title: trimmed, aspect, style });
          }}
        >
          <div>
            <label htmlFor="project-title" className="mb-1 block text-sm font-medium text-fg">
              标题
            </label>
            <input
              ref={titleInputRef}
              id="project-title"
              name="title"
              value={title}
              onChange={(event) => {
                setTitle(event.target.value);
                if (titleError) setTitleError(null);
              }}
              maxLength={60}
              placeholder="例如：仙大"
              aria-invalid={titleError ? true : undefined}
              aria-describedby={titleError ? "project-title-error" : undefined}
              className="dp-input w-full"
            />
            {titleError ? (
              <p id="project-title-error" role="alert" className="mt-1 text-xs text-danger">
                {titleError}
              </p>
            ) : null}
          </div>

          <fieldset>
            <legend className="mb-2 block text-sm font-medium text-fg">画幅</legend>
            <div className="grid grid-cols-3 gap-2">
              {ASPECT_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className="cursor-pointer rounded-lg border border-border bg-bg px-3 py-2 text-center text-sm text-muted focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-accent has-[:checked]:border-accent has-[:checked]:bg-accent-soft has-[:checked]:text-fg"
                >
                  <input
                    type="radio"
                    name="aspect"
                    value={option.value}
                    checked={aspect === option.value}
                    onChange={() => setAspect(option.value)}
                    className="sr-only"
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="mb-2 block text-sm font-medium text-fg">风格</legend>
            <div className="grid grid-cols-2 gap-2">
              {STYLE_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className="cursor-pointer rounded-lg border border-border bg-bg px-3 py-2 text-center text-sm text-muted focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-accent has-[:checked]:border-accent has-[:checked]:bg-accent-soft has-[:checked]:text-fg"
                >
                  <input
                    type="radio"
                    name="style"
                    value={option.value}
                    checked={style === option.value}
                    onChange={() => setStyle(option.value)}
                    className="sr-only"
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </fieldset>

          {mutationError ? (
            <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">
              {mutationError}
            </p>
          ) : null}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="dp-btn"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="dp-btn primary"
            >
              {mutation.isPending ? "创建中…" : "创建项目"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
